import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ChefHat, Sparkles, Minus, Plus, ArrowLeft, AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import type { LocalRecipe } from "@shared/schema";

type RecipeWithMissing = LocalRecipe & {
  missingIngredients: string[];
  coverage: { sellerIds: string[]; stops: number; unmatchedNames: string[] };
};

function estimateFeeForStops(stops: number): number {
  if (stops <= 0) return 0;
  return 6 + 3 * stops;
}

interface RecipesResponse {
  generatedAt: string | null;
  ingredientCount: number;
  recipes: RecipeWithMissing[];
}

const DIETARY_OPTIONS: { value: string; label: string }[] = [
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "gluten_free", label: "Gluten-free" },
  { value: "dairy_free", label: "Dairy-free" },
];

function dietaryLabel(tag: string): string {
  return DIETARY_OPTIONS.find((o) => o.value === tag)?.label ?? tag;
}

export default function RecipePicker() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const params = new URLSearchParams(search);
  const zipFromQuery = params.get("zip")?.trim() || "";
  const zip = zipFromQuery || (user?.zipCode?.trim() ?? "");

  const [selections, setSelections] = useState<Map<string, number>>(new Map());
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, refetch, isFetching } = useQuery<RecipesResponse>({
    queryKey: ["/api/local-recipes", zip],
    queryFn: async () => {
      const res = await fetch(`/api/local-recipes?zip=${encodeURIComponent(zip)}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || "Failed to load recipes");
      }
      return res.json();
    },
    enabled: Boolean(zip),
  });

  const filteredRecipes = useMemo(() => {
    const all = data?.recipes ?? [];
    if (activeFilters.size === 0) return all;
    return all.filter((recipe) =>
      Array.from(activeFilters).every((tag) => recipe.dietaryTags.includes(tag)),
    );
  }, [data, activeFilters]);

  const combinedEstimate = useMemo(() => {
    if (selections.size === 0 || !data?.recipes) {
      return { stops: 0, fee: 0 };
    }
    const byId = new Map(data.recipes.map((r) => [r.id, r]));
    const sellerSet = new Set<string>();
    for (const recipeId of selections.keys()) {
      const recipe = byId.get(recipeId);
      if (!recipe) continue;
      for (const sellerId of recipe.coverage.sellerIds) sellerSet.add(sellerId);
    }
    const stops = sellerSet.size;
    return { stops, fee: estimateFeeForStops(stops) };
  }, [selections, data]);

  const toggleFilter = (tag: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const toggleSelected = (recipe: LocalRecipe) => {
    setSelections((prev) => {
      const next = new Map(prev);
      if (next.has(recipe.id)) next.delete(recipe.id);
      else next.set(recipe.id, recipe.servingsDefault);
      return next;
    });
  };

  const adjustServings = (recipeId: string, delta: number, defaultValue: number) => {
    setSelections((prev) => {
      const next = new Map(prev);
      const current = next.get(recipeId) ?? defaultValue;
      const updated = Math.max(1, Math.min(20, current + delta));
      next.set(recipeId, updated);
      return next;
    });
  };

  const handleBuildBox = () => {
    if (selections.size === 0) return;
    const payload = {
      zip,
      selections: Array.from(selections.entries()).map(([recipeId, servings]) => ({
        recipeId,
        servings,
      })),
    };
    sessionStorage.setItem("recipeBoxSelection", JSON.stringify(payload));
    setLocation("/box-preview");
  };

  if (!zip) {
    return (
      <>
        <Header />
        <div className="container mx-auto max-w-2xl px-4 py-10 text-center">
          <h1 className="text-xl font-semibold">Tell us your ZIP first</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Add your ZIP in your account so we can pull what your neighbors have on offer.
          </p>
          <Link href="/account">
            <Button className="mt-4">Update ZIP</Button>
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container mx-auto max-w-3xl px-4 pt-4 pb-32 sm:pt-6">
        <div className="mb-4 flex items-center gap-2">
          <Link href="/" className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <div className="flex items-center gap-1.5">
              <h1 className="text-xl font-semibold sm:text-2xl">Cook this week</h1>
              <Sparkles className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-xs text-muted-foreground sm:text-sm">
              Recipes from what neighbors near {zip} have right now. Pick what looks good and we&rsquo;ll build the box.
            </p>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {DIETARY_OPTIONS.map((opt) => {
            const active = activeFilters.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleFilter(opt.value)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:bg-muted"
                }`}
                data-testid={`filter-${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center text-sm text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span>Cooking up recipes from local ingredients&hellip;</span>
            <span className="text-xs">First time today can take a few seconds.</span>
          </div>
        ) : isError ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-medium">Recipes are taking a quick break</div>
                <p className="mt-1 text-xs">
                  We couldn&rsquo;t whip up this week&rsquo;s recipes right now. Check back in a bit.
                </p>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </div>
          </div>
        ) : filteredRecipes.length === 0 ? (
          <div className="rounded-xl border bg-card p-6 text-center text-sm text-muted-foreground">
            {data?.recipes.length === 0
              ? "No recipes available yet. Check back when more neighbors are listing."
              : "No recipes match those filters. Try removing a filter."}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {filteredRecipes.map((recipe) => {
              const selected = selections.has(recipe.id);
              const servings = selections.get(recipe.id) ?? recipe.servingsDefault;
              const hero = recipe.ingredients.filter((i) => !i.pantryStaple).slice(0, 3);
              const missingCount = recipe.missingIngredients.length;
              const recipeFee = estimateFeeForStops(recipe.coverage.stops);
              const stopsLabel =
                recipe.coverage.stops === 0
                  ? "Ingredients at home"
                  : `${recipe.coverage.stops} stop${recipe.coverage.stops !== 1 ? "s" : ""} · ~$${recipeFee} delivery`;

              return (
                <Card
                  key={recipe.id}
                  className={`cursor-pointer overflow-hidden transition ${
                    selected ? "border-primary shadow-md ring-1 ring-primary/40" : "hover:shadow-sm"
                  }`}
                  onClick={() => toggleSelected(recipe)}
                  data-testid={`recipe-card-${recipe.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <ChefHat className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold leading-tight sm:text-base">{recipe.title}</h3>
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                          {recipe.description}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      <span>{recipe.prepMinutes} min</span>
                      <span>Serves {recipe.servingsDefault}</span>
                      {recipe.dietaryTags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground"
                        >
                          {dietaryLabel(tag)}
                        </span>
                      ))}
                    </div>

                    <div className="mt-2 inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-[11px] text-foreground">
                      {stopsLabel}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {hero.map((ing) => (
                        <span
                          key={ing.name}
                          className="rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-[11px] text-foreground"
                        >
                          {ing.name}
                        </span>
                      ))}
                    </div>

                    {missingCount > 0 ? (
                      <div className="mt-3 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                        Missing locally: {recipe.missingIngredients.slice(0, 3).join(", ")}
                        {missingCount > 3 ? `, +${missingCount - 3} more` : ""}
                      </div>
                    ) : null}

                    {selected ? (
                      <div className="mt-3 flex items-center justify-between rounded-lg border border-primary/40 bg-primary/5 px-2 py-1.5">
                        <span className="text-xs font-medium">Servings: {servings}</span>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              adjustServings(recipe.id, -1, recipe.servingsDefault);
                            }}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              adjustServings(recipe.id, 1, recipe.servingsDefault);
                            }}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {data && data.recipes.length > 0 ? (
          <p className="mt-4 text-center text-[11px] text-muted-foreground">
            {data.ingredientCount} local ingredients available
            {isFetching ? " · refreshing" : ""}
          </p>
        ) : null}
      </div>

      {selections.size > 0 ? (
        <div
          className="fixed inset-x-0 z-30 border-t bg-background/95 px-4 py-3 backdrop-blur"
          style={{ bottom: 52 }}
        >
          <div className="container mx-auto flex max-w-3xl items-center justify-between gap-3">
            <div className="min-w-0 text-sm">
              <div>
                <span className="font-semibold">{selections.size}</span>
                <span className="text-muted-foreground">
                  {" "}
                  recipe{selections.size !== 1 ? "s" : ""} ·{" "}
                </span>
                <span className="font-medium">
                  {combinedEstimate.stops} stop{combinedEstimate.stops !== 1 ? "s" : ""}
                </span>
              </div>
              {combinedEstimate.stops > 0 ? (
                <div className="text-[11px] text-muted-foreground">
                  ~${combinedEstimate.fee} delivery · tip included
                </div>
              ) : null}
            </div>
            <Button onClick={handleBuildBox} data-testid="button-build-box">
              Build my box
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
